import z from "zod";

import {
  item,
  withMeaning,
  type AnswerOptionMeaning,
} from "../../authoring.js";
import {
  actionLevel,
  actionLevelOrNotApplicable,
  actionLevelOrUnknown,
  yesNo,
} from "./responseSets.js";

/**
 * A version of the placeholder instrument that asks every kind of answer a form
 * collects and gives everything it asks a meaning, so the machinery around
 * meanings and decorations has something real to work on.
 *
 * Placeholder content, not anybody's assessment: the words are made up.
 */

export const name = ["sample", "core", "1.2", "r1"] as const;

export const groups = {
  life_functioning: "life_functioning",
  trauma: "trauma",
  review: "review",
} as const;

export const items = {
  school_attendance: "school_attendance",
  school_engagement: "school_engagement",
  family_history: "family_history",
  adjustment_to_trauma: "adjustment_to_trauma",
  last_reviewed_on: "last_reviewed_on",
  key_worker: "key_worker",
  summary: "summary",
} as const;

/** Retired by 1.1, and never asked again. */
export const retiredItems = ["school_behaviour"] as const;

/** The four levels, worded once for every item rated on them. */
const levels = [
  { value: 0, title: { en: "No evidence of need" } },
  { value: 1, title: { en: "Watch or prevent" } },
  { value: 2, title: { en: "Action needed" } },
  { value: 3, title: { en: "Immediate action needed" } },
] as const satisfies readonly AnswerOptionMeaning<0 | 1 | 2 | 3>[];

const lifeFunctioning = withMeaning(
  z
    .object({
      ...item(items.school_attendance, actionLevelOrNotApplicable, {
        title: { en: "School attendance" },
        description: {
          en: "How regularly the young person attends school or college.",
        },
        answers: [
          ...levels,
          { value: "not_applicable", title: { en: "Not in education" } },
        ],
      })
        .withComment()
        .build(),
      ...item(items.school_engagement, actionLevel, {
        title: { en: "Engagement in school" },
        answers: levels,
      })
        .optional()
        .withComment()
        .build(),
      ...item(items.family_history, yesNo, {
        title: { en: "Family history of missed schooling" },
        answers: [
          { value: "yes", title: { en: "Yes" } },
          { value: "no", title: { en: "No" } },
        ],
      })
        .withComment()
        .build(),
    })
    .strict(),
  { title: { en: "Life functioning" } },
);

const trauma = withMeaning(
  z
    .object({
      ...item(items.adjustment_to_trauma, actionLevelOrUnknown, {
        title: { en: "Adjustment to trauma" },
        answers: [...levels, { value: "unknown", title: { en: "Not known" } }],
      })
        .withComment()
        .build(),
    })
    .strict(),
  {
    title: { en: "Trauma" },
    description: { en: "Asked where the young person has experienced trauma." },
  },
);

const review = withMeaning(
  z
    .object({
      // A string rather than a stricter type: a record uploaded from another
      // system may say "March 2019", and must still be a valid document.
      ...item(items.last_reviewed_on, z.string(), {
        title: { en: "Date of last review" },
      })
        .withComment()
        .build(),
      ...item(items.key_worker, z.string(), {
        title: { en: "Key worker" },
      })
        .optional()
        .build(),
      ...item(items.summary, z.string(), {
        title: { en: "Summary of needs" },
      }).build(),
    })
    .strict(),
  { title: { en: "Review" } },
);

export const schema = z
  .object({
    [groups.life_functioning]: lifeFunctioning,
    [groups.trauma]: trauma.optional(),
    [groups.review]: review,
  })
  .strict();
